import { useId, useState, type ReactNode } from "react";
import {
  dayKey, daysToDeadline, entryDuration, formatMoney, isLive, isTaskOpen, orderedTasks, parentTask, projectCurrency, projectTotals, runningEntry, sortCurrencies, startTimer, subTasks,
  type Dataset, type MoneyByCurrency, type Range, type TimeEntry
} from "@otrack/domain";
import { Icon, type IconName } from "@otrack/ui";
import type { AssistantSource } from "./assistant";
import { billingSummary } from "./billing-labels";
import { colorOf } from "./calendar/model";
import { projectLabel, targetLabel } from "./commands";
import { useApp, useDataset, useFactory } from "./context";
import { byRecent, InvoiceRow } from "./invoice-list";
import { openTasks } from "./lookups";
import { deadlineLabel } from "./project-card";
import { href } from "./router";
import { formatClock, formatDuration, formatMonth, MINUTE, monthRange } from "./time";

const SHOWN = 6;
const shortDay = new Intl.DateTimeFormat("en-US", { weekday: "short", month: "short", day: "numeric" });
const sinceDay = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric" });

function moneyList(money: MoneyByCurrency): string {
  return sortCurrencies(Object.keys(money)).filter((currency) => money[currency]).map((currency) => formatMoney({ amount: money[currency]!, currency })).join(", ");
}

/** "2026-10-19" as "Mon, Oct 19". */
function dayLabel(iso: string): string {
  const [y = 1970, m = 1, d = 1] = iso.split("-").map(Number);
  return shortDay.format(new Date(y, m - 1, d));
}

/** A titled group of records, with a way to see the rest when only some are shown. */
function Source({ icon, title, more, children }: { icon: IconName; title: string; more?: { label: string; href: string } | null; children: ReactNode }) {
  return <section className="ot-source">
    <header className="ot-source__head">
      <Icon name={icon}/>
      <h4>{title}</h4>
      {more && <a className="ot-source__more" href={more.href}>{more.label}<Icon name="arrow-right"/></a>}
    </header>
    {children}
  </section>;
}

function counted(shown: number, total: number, noun: string, plural = `${noun}s`): string {
  const word = total === 1 ? noun : plural;
  return shown < total ? `${shown} of ${total} ${word}` : `${total} ${word}`;
}

function Entries({ source }: { source: Extract<AssistantSource, { kind: "entries" }> }) {
  const { now } = useApp();
  const dataset = useDataset();
  const entries = source.ids.map((id) => dataset.timeEntries[id]).filter((entry) => entry && isLive(entry)).slice(0, SHOWN);
  if (!entries.length) return null;
  return <Source icon="clock" title={counted(entries.length, source.total, "entry", "entries")} more={{ label: "Open calendar", href: href({ page: "calendar", view: "week", date: dayKey(entries[0]!.start) }) }}>
    <ol className="ot-day__list">
      {entries.map((entry) => {
        const project = entry!.projectId ? dataset.projects[entry!.projectId] : undefined;
        const client = project?.clientId ? dataset.clients[project.clientId] : undefined;
        return <li key={entry!.id} className={entry!.end === null ? "is-running" : ""} style={{ ["--pc" as string]: colorOf(dataset, entry!.projectId) }}>
          <span className="ot-swatch"/>
          <span className="ot-day__what">
            <b>{project?.name ?? "No project"}</b>
            <span>{[client?.name, entry!.description].filter(Boolean).join(", ")}</span>
          </span>
          <span className="ot-day__when ot-mono">{shortDay.format(entry!.start)}<i/>{formatClock(entry!.start)}<i>to</i>{entry!.end === null ? "now" : formatClock(entry!.end)}</span>
          <span className="ot-day__took ot-mono">{formatDuration(entryDuration(entry!, now))}</span>
        </li>;
      })}
    </ol>
  </Source>;
}

const GROUP_NOUN = { project: "project", client: "client", task: "task", day: "day" } as const;

function Summary({ source }: { source: Extract<AssistantSource, { kind: "summary" }> }) {
  const dataset = useDataset();
  if (!source.rows.length) return null;
  const rows = source.rows.slice(0, 8);
  const top = Math.max(...rows.map((row) => row.ms), 1);
  const color = (key: string) => source.groupBy === "project" && dataset.projects[key] ? colorOf(dataset, key) : "color-mix(in srgb, var(--ot-ink) 32%, transparent)";
  return <Source icon="clock" title={counted(rows.length, source.rows.length, `${GROUP_NOUN[source.groupBy]}`)}>
    <ul className="ot-earn__shares">
      {rows.map((row, index) => <li key={row.key} style={{ ["--pc" as string]: color(row.key), ["--share" as string]: row.ms / top, ["--i" as string]: index }}>
        <span className="ot-swatch"/>
        <span className="ot-earn__who"><b>{source.groupBy === "day" ? dayLabel(row.label) : row.label}</b></span>
        <span className="ot-earn__hours ot-mono">{formatDuration(row.ms)}</span>
        <span className="ot-earn__amount ot-mono">{moneyList(row.money) || <span className="ot-source__none">Not billed</span>}</span>
        <i className="ot-earn__bar" aria-hidden="true"/>
      </li>)}
    </ul>
  </Source>;
}

function Invoices({ source }: { source: Extract<AssistantSource, { kind: "invoices" }> }) {
  const { now } = useApp();
  const dataset = useDataset();
  const invoices = source.ids.map((id) => dataset.invoices[id]).filter((invoice) => invoice && isLive(invoice)).map((invoice) => invoice!).sort(byRecent).slice(0, 5);
  if (!invoices.length) return null;
  return <Source icon="receipt" title={counted(invoices.length, source.total, "invoice")} more={{ label: "All invoices", href: href({ page: "invoices" }) }}>
    <div className="ot-source__invoices">
      {invoices.map((invoice, index) => <InvoiceRow key={invoice.id} invoice={invoice} dataset={dataset} now={now} index={index}/>)}
    </div>
  </Source>;
}

function Uninvoiced({ source, onPick }: { source: Extract<AssistantSource, { kind: "uninvoiced" }>; onPick: (command: string) => void }) {
  if (!source.clients.length) return null;
  return <Source icon="hand-coins" title="Not invoiced yet">
    <ul className="ot-owed">
      {source.clients.map((client, index) => <li key={client.clientId} style={{ ["--i" as string]: index }}>
        <span className="ot-owed__who"><b>{client.name}</b>{client.oldest !== null && <span>since {sinceDay.format(client.oldest)}</span>}</span>
        <span className="ot-owed__hours ot-mono">{formatDuration(client.ms)}</span>
        <span className="ot-owed__amount ot-mono">{moneyList(client.money)}</span>
        <button type="button" className="ot-chip" onClick={() => onPick(`draft an invoice for ${client.name}`)}>Draft invoice</button>
      </li>)}
    </ul>
  </Source>;
}

function Events({ source }: { source: Extract<AssistantSource, { kind: "events" }> }) {
  const dataset = useDataset();
  const items = source.items.slice(0, SHOWN);
  if (!items.length) return null;
  return <Source icon="calendar" title={counted(items.length, source.total, "plan")} more={{ label: "Open calendar", href: href({ page: "calendar", view: "week", date: dayKey(items[0]!.start) }) }}>
    <ol className="ot-plans">
      {items.map((item) => <li key={item.key} style={{ ["--pc" as string]: colorOf(dataset, item.projectId) }}>
        <span className="ot-plans__when ot-mono">{shortDay.format(item.start)}<i/>{item.allDay ? "All day" : formatClock(item.start)}</span>
        <span className="ot-swatch"/>
        <span className="ot-day__what">
          <b>{item.title}</b>
          {item.projectId ? <span>{dataset.projects[item.projectId]?.name}</span> : item.calendar ? <span>{item.calendar}</span> : null}
        </span>
        <span className={`ot-plans__logged ${item.logged ? "is-logged" : ""}`}>
          {item.allDay ? (item.logged ? `${formatDuration(item.logged)} logged` : "") : item.logged ? <><b className="ot-mono">{formatDuration(item.logged)}</b> of {formatDuration(item.end - item.start)}</> : <>{formatDuration(item.end - item.start)} planned</>}
        </span>
      </li>)}
    </ol>
  </Source>;
}

/** Starts a Timer on a Project or Task, or shows that one already runs there. */
function Start({ projectId, taskId, name }: { projectId: string; taskId: string | null; name: string }) {
  const { act } = useApp();
  const dataset = useDataset();
  const factory = useFactory();
  const running = runningEntry(dataset);
  if (running && running.projectId === projectId && running.taskId === taskId) {
    return <span className="ot-start is-running"><span className="ot-chip__live" aria-hidden="true"/>Running</span>;
  }
  const start = () => {
    const label = targetLabel(dataset, projectId, taskId);
    void act(startTimer(dataset, { projectId, taskId }, factory, Date.now()), running ? `Switched to ${label}` : `Started ${label}`);
  };
  return <button type="button" className="ot-chip ot-start" aria-label={`Start ${name}`} onClick={start}>
    <Icon name="play"/>Start
  </button>;
}

type TaskItem = Extract<AssistantSource, { kind: "tasks" }>["items"][number];
interface TaskGroup { task: TaskItem; subs: TaskItem[] }

/** Open Tasks with their open Sub-tasks gathered under them; lookups list a parent before its Sub-tasks. */
function nestTasks(dataset: Dataset, items: TaskItem[]): TaskGroup[] {
  const groups: TaskGroup[] = [];
  const byId = new Map<string, TaskGroup>();
  for (const item of items) {
    const parentId = dataset.tasks[item.id]?.parentId;
    const parent = parentId ? byId.get(parentId) : undefined;
    if (parent) parent.subs.push(item);
    else {
      const group = { task: item, subs: [] };
      groups.push(group);
      byId.set(item.id, group);
    }
  }
  return groups;
}

const plural = (count: number, noun: string) => `${count} ${count === 1 ? noun : `${noun}s`}`;

/** When matching time was last worked: "Now" while it runs, otherwise the day and time it started. */
function lastWorked(dataset: Dataset, match: (entry: TimeEntry) => boolean): string {
  let last: TimeEntry | null = null;
  for (const entry of Object.values(dataset.timeEntries)) {
    if (isLive(entry) && match(entry) && (!last || entry.start > last.start)) last = entry;
  }
  if (!last) return "Not yet";
  return last.end === null ? "Now" : `${shortDay.format(last.start)}, ${formatClock(last.start)}`;
}

/** The facts behind a row, with the way to its Project page. */
function Facts({ id, projectId, facts }: { id: string; projectId: string; facts: Array<[string, ReactNode] | null | false> }) {
  return <div id={id} className="ot-todo__details">
    <dl className="ot-facts">
      {facts.filter((fact): fact is [string, ReactNode] => !!fact).map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{value}</dd></div>)}
    </dl>
    <a className="ot-source__more" href={href({ page: "project", id: projectId })}>Open project<Icon name="arrow-right"/></a>
  </div>;
}

function ProjectFacts({ id, projectId }: { id: string; projectId: string }) {
  const { now } = useApp();
  const dataset = useDataset();
  const project = dataset.projects[projectId]!;
  const client = project.clientId ? dataset.clients[project.clientId] : undefined;
  const days = daysToDeadline(project, now);
  const tasks = orderedTasks(dataset, projectId, (task) => isTaskOpen(dataset, task));
  const subs = tasks.filter((task) => task.parentId !== null).length;
  const billed = project.billingMode !== "non-billable";
  const spent = (range?: Range) => {
    const totals = projectTotals(dataset, projectId, now, range);
    return billed ? `${formatDuration(totals.tracked)} · ${formatMoney({ amount: totals.earned, currency: totals.currency })}` : formatDuration(totals.tracked);
  };
  return <Facts id={id} projectId={projectId} facts={[
    client ? ["Client", client.name] : null,
    ["Billing", billingSummary(dataset, project)],
    project.deadline !== null && days !== null && ["Deadline", `${dayLabel(project.deadline)}, ${deadlineLabel(days).toLowerCase()}`],
    [formatMonth(now), spent(monthRange(now))],
    ["All time", spent()],
    ["Open tasks", tasks.length ? `${tasks.length - subs}${subs ? `, ${plural(subs, "sub-task")}` : ""}` : "None"],
    ["Last worked", lastWorked(dataset, (entry) => entry.projectId === projectId)]
  ]}/>;
}

function TaskFacts({ id, taskId }: { id: string; taskId: string }) {
  const { now } = useApp();
  const dataset = useDataset();
  const task = dataset.tasks[taskId]!;
  const project = dataset.projects[task.projectId];
  const parent = parentTask(dataset, task);
  const children = parent ? [] : subTasks(dataset, taskId);
  // A Task's time includes its Sub-tasks', so the Estimate covers all of it.
  const ids = new Set([taskId, ...children.map((child) => child.id)]);
  let logged = 0;
  for (const entry of Object.values(dataset.timeEntries)) if (isLive(entry) && entry.taskId && ids.has(entry.taskId)) logged += entryDuration(entry, now);
  const estimate = task.estimateMinutes === null ? null : task.estimateMinutes * MINUTE;
  const left = estimate === null ? "" : logged > estimate ? `, ${formatDuration(logged - estimate)} over` : `, ${formatDuration(estimate - logged)} left`;
  return <Facts id={id} projectId={task.projectId} facts={[
    ["Project", projectLabel(dataset, task.projectId)],
    parent ? ["Sub-task of", parent.name] : null,
    ["Logged", estimate === null ? formatDuration(logged) : `${formatDuration(logged)} of ${formatDuration(estimate)}${left}`],
    project?.billingMode === "fixed" && task.fixedPrice !== null && ["Fixed price", formatMoney({ amount: task.fixedPrice, currency: projectCurrency(dataset, project) })],
    children.length > 0 && ["Sub-tasks", `${children.filter((child) => isTaskOpen(dataset, child)).length} open of ${children.length}`],
    ["Last worked", lastWorked(dataset, (entry) => entry.taskId !== null && ids.has(entry.taskId))]
  ]}/>;
}

/**
 * A Project or Task with its time and a way to start it. Clicking the name reveals what is
 * inside it; Details shows its facts.
 */
function Row({ projectId, taskId, name, meta, due, time, index, details, inside }: {
  projectId: string;
  taskId: string | null;
  name: string;
  meta?: string;
  due?: number | null | undefined;
  time: ReactNode;
  index: number;
  details: (id: string) => ReactNode;
  /** The rows the name reveals; null when there is nothing inside. */
  inside: (() => ReactNode) | null;
}) {
  const dataset = useDataset();
  const [open, setOpen] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const detailsId = useId();
  const title = <><b>{name}</b>{meta && <span>{meta}</span>}</>;
  return <li style={{ ["--pc" as string]: colorOf(dataset, projectId), ["--i" as string]: index }}>
    <span className="ot-swatch"/>
    {inside
      ? <button type="button" className="ot-todo__what" aria-expanded={open} onClick={() => setOpen(!open)}>
        <Icon name="caret-right" weight="bold" className="ot-todo__caret"/>{title}
      </button>
      : <span className="ot-todo__what">{title}</span>}
    {due != null && <span className={`ot-todo__due ${due < 0 ? "is-late" : due <= 3 ? "is-soon" : ""}`}>{deadlineLabel(due)}</span>}
    <span className="ot-todo__time ot-mono">{time}</span>
    <button type="button" className="ot-todo__toggle" aria-expanded={showDetails} aria-controls={showDetails ? detailsId : undefined} aria-label={`Details of ${name}`} onClick={() => setShowDetails(!showDetails)}>
      Details<Icon name="caret-down" weight="bold" className="ot-todo__caret"/>
    </button>
    <Start projectId={projectId} taskId={taskId} name={name}/>
    {showDetails && details(detailsId)}
    {open && inside?.()}
  </li>;
}

/** An open Task, with its open Sub-tasks behind its name. */
function TaskRow({ item, name, meta, due, subs, index }: { item: TaskItem; name: string; meta?: string; due?: number | null | undefined; subs: TaskItem[]; index: number }) {
  const dataset = useDataset();
  return <Row
    projectId={item.projectId}
    taskId={item.id}
    name={name}
    meta={[meta, subs.length ? plural(subs.length, "sub-task") : null].filter(Boolean).join(" · ")}
    due={due}
    time={item.estimate !== null ? <><b>{formatDuration(item.logged)}</b> of {formatDuration(item.estimate)}</> : item.logged ? <b>{formatDuration(item.logged)}</b> : ""}
    index={index}
    details={(id) => <TaskFacts id={id} taskId={item.id}/>}
    inside={subs.length ? () => <ul className="ot-todo ot-todo--startable ot-todo--nested">
      {subs.map((sub, at) => <TaskRow key={sub.id} item={sub} name={dataset.tasks[sub.id]?.name ?? sub.name} subs={[]} index={at}/>)}
    </ul> : null}
  />;
}

/** A Project in progress, with its open Tasks behind its name. */
function ProjectRow({ item, index }: { item: Extract<AssistantSource, { kind: "projects" }>["items"][number]; index: number }) {
  const { now } = useApp();
  const dataset = useDataset();
  const project = dataset.projects[item.id]!;
  const client = project.clientId ? dataset.clients[project.clientId] : undefined;
  return <Row
    projectId={project.id}
    taskId={null}
    name={project.name}
    meta={[client?.name, item.openTasks ? plural(item.openTasks, "task") : null].filter(Boolean).join(" · ")}
    due={daysToDeadline(project, now)}
    time={item.tracked ? <b>{formatDuration(item.tracked)}</b> : ""}
    index={index}
    details={(id) => <ProjectFacts id={id} projectId={project.id}/>}
    inside={item.openTasks ? () => <ul className="ot-todo ot-todo--startable ot-todo--nested">
      {nestTasks(dataset, openTasks(dataset, now, project.id).items).map((group, at) => <TaskRow key={group.task.id} item={group.task} name={group.task.name} subs={group.subs} index={at}/>)}
    </ul> : null}
  />;
}

function Projects({ source }: { source: Extract<AssistantSource, { kind: "projects" }> }) {
  const dataset = useDataset();
  const items = source.items.filter((item) => dataset.projects[item.id]).slice(0, 8);
  if (!items.length) return null;
  return <Source icon="folder" title={counted(items.length, source.total, "project")} more={{ label: "All projects", href: href({ page: "projects" }) }}>
    <ul className="ot-todo ot-todo--startable">
      {items.map((item, index) => <ProjectRow key={item.id} item={item} index={index}/>)}
    </ul>
  </Source>;
}

function Tasks({ source }: { source: Extract<AssistantSource, { kind: "tasks" }> }) {
  const { now } = useApp();
  const dataset = useDataset();
  const all = nestTasks(dataset, source.items.filter((item) => dataset.tasks[item.id] && dataset.projects[item.projectId]));
  const groups = all.slice(0, 8);
  if (!groups.length) return null;
  const projectIds = new Set(source.items.map((item) => item.projectId));
  const only = projectIds.size === 1 ? [...projectIds][0]! : null;
  return <Source icon="check-circle" title={counted(groups.length, all.length, "open task")} more={only ? { label: "Open project", href: href({ page: "project", id: only }) } : { label: "All projects", href: href({ page: "projects" }) }}>
    <ul className="ot-todo ot-todo--startable">
      {groups.map(({ task, subs }, index) => {
        const project = dataset.projects[task.projectId]!;
        const client = project.clientId ? dataset.clients[project.clientId] : undefined;
        return <TaskRow
          key={task.id}
          item={task}
          name={task.name}
          meta={[client?.name, project.name].filter(Boolean).join(", ")}
          due={daysToDeadline(project, now)}
          subs={subs}
          index={index}
        />;
      })}
    </ul>
  </Source>;
}

/** The records behind an assistant reply. */
export function SourceCards({ sources, onPick }: { sources: AssistantSource[]; onPick: (command: string) => void }) {
  return <>{sources.map((source) => {
    switch (source.kind) {
      case "entries": return <Entries key="entries" source={source}/>;
      case "summary": return <Summary key="summary" source={source}/>;
      case "invoices": return <Invoices key="invoices" source={source}/>;
      case "uninvoiced": return <Uninvoiced key="uninvoiced" source={source} onPick={onPick}/>;
      case "events": return <Events key="events" source={source}/>;
      case "tasks": return <Tasks key="tasks" source={source}/>;
      case "projects": return <Projects key="projects" source={source}/>;
    }
  })}</>;
}
